// Launch-candidate API surface. Transport-only passthrough through the bridge to
// the real production backend (/launch/* and /billing/*). Reuses the existing
// token/auth mechanism (apiRequest attaches the Bearer + silent refresh on 401).
// No mock data: every field shown by the UI comes from the real backend response.
import { apiRequest } from "./client";

const LAUNCH_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export type PlanCode = "FREE" | "PRO" | "ELITE" | "PREMIUM";

export interface TrialState {
  active?: boolean;
  ends_at?: string | null;
  seconds_remaining?: number | null;
  consumed?: boolean;
}
export interface LaunchMe {
  base_plan?: PlanCode | string;
  effective_plan?: PlanCode | string;
  paid_subscription?: boolean;
  trial?: TrialState;
  features?: Record<string, boolean> | string[];
  limits?: Record<string, unknown>;
  usage_limits?: Record<string, unknown>;
  [k: string]: unknown;
}

function req<T>(path: string, opts: Parameters<typeof apiRequest>[1] = {}) {
  return apiRequest<T>(path, { base: LAUNCH_BASE, auth: true, retries: 0, ...opts });
}

// ---- membership / trial ----
export const fetchLaunchMe = () => req<LaunchMe>("/launch/me");
export const startTrial = () => req<LaunchMe>("/launch/trial/start", { method: "POST", body: {} });

// ---- analytics events (fire-and-forget; never block UI) ----
export function postLaunchEvent(event_name: string, metadata?: Record<string, unknown>) {
  req<unknown>("/launch/events", { method: "POST", body: { event_name, metadata: metadata || {} } }).catch(() => {});
}

// ---- Stripe (backend-owned; we only call and redirect to the returned URL) ----
export async function stripeCheckout(plan: "pro" | "elite" | "premium", period: "monthly" | "yearly"): Promise<string> {
  const r = await req<any>("/billing/stripe/checkout", { method: "POST", body: { plan, period } });
  return r?.url ?? r?.checkout_url ?? r?.redirect_url ?? "";
}
export async function stripePortal(): Promise<string> {
  const r = await req<any>("/billing/stripe/portal", { method: "POST", body: {} });
  return r?.url ?? r?.portal_url ?? r?.redirect_url ?? "";
}

// ---- coupon center ----
export const fetchCoupon = () => req<any>("/launch/coupon");

// ---- support ----
export const listTickets = () => req<any>("/launch/support/tickets");
export const createTicket = (body: { category: string; subject: string; message: string }) =>
  req<any>("/launch/support/tickets", { method: "POST", body });
export const listTicketMessages = (id: string) => req<any>(`/launch/support/tickets/${id}/messages`);
export const postTicketMessage = (id: string, message: string) =>
  req<any>(`/launch/support/tickets/${id}/messages`, { method: "POST", body: { message } });

// ---- admin ----
export const adminListUsers = (q?: string) => req<any>(`/launch/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`);
export const adminPatchUserPlan = (id: string, value: PlanCode) =>
  req<any>(`/launch/admin/users/${id}/plan`, { method: "PATCH", body: { value } });
export const adminPatchUserStatus = (id: string, value: "ACTIVE" | "SUSPENDED") =>
  req<any>(`/launch/admin/users/${id}/status`, { method: "PATCH", body: { value } });
export const adminListTickets = () => req<any>("/launch/admin/tickets");
export const adminPatchTicket = (id: string, body: Record<string, unknown>) =>
  req<any>(`/launch/admin/tickets/${id}`, { method: "PATCH", body });
export const adminReplyTicket = (id: string, message: string) =>
  req<any>(`/launch/admin/tickets/${id}/reply`, { method: "POST", body: { message } });

// ---- helpers ----
export const PLAN_RANK: Record<string, number> = { FREE: 0, PRO: 1, ELITE: 2, PREMIUM: 3 };
export function planRank(p?: string | null): number {
  return p ? (PLAN_RANK[String(p).toUpperCase()] ?? -1) : -1;
}
